import React, { useState, useEffect } from 'react';
import { UserIcon, EnvelopeIcon, BuildingOffice2Icon, CalendarIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';
import authService from '../../services/auth';

const ProfileTab = ({ onNotify }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    company_name: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getProfile();
      setProfile(data);
      setFormData({
        email: data.email || '',
        company_name: data.company_name || '',
      });
    } catch (err) {
      onNotify('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await settingsService.updateProfile(formData);
      setProfile(updated);
      onNotify('Profile updated successfully');
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const initials = (profile?.username || 'U').substring(0, 2).toUpperCase();

  return (
    <div className="space-y-6">
      {/* Profile Header Card */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xl font-bold text-white">
            {initials}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">{profile?.username}</h2>
            <p className="text-gray-400 text-sm">{profile?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                profile?.is_active
                  ? 'bg-green-900/30 text-green-400 border border-green-800'
                  : 'bg-red-900/30 text-red-400 border border-red-800'
              }`}>
                {profile?.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-900/30 text-blue-400 border border-blue-800 capitalize">
                {profile?.role}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Read-only Info */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <IdentificationIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">User ID</p>
              <p className="text-sm text-gray-300">{profile?.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <UserIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Username</p>
              <p className="text-sm text-gray-300">{profile?.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <BuildingOffice2Icon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Company ID</p>
              <p className="text-sm text-gray-300">{profile?.company_id ?? '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Member Since</p>
              <p className="text-sm text-gray-300">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Editable Form */}
      <form onSubmit={handleSave} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Edit Profile</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" aria-hidden="true" />
              <input
                id="profile-email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <label htmlFor="profile-company" className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
            <div className="relative">
              <BuildingOffice2Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" aria-hidden="true" />
              <input
                id="profile-company"
                name="company_name"
                type="text"
                autoComplete="organization"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your company name"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition-colors motion-reduce:transition-none"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileTab;
